/**
 * Booking Reference Generator Utility
 * Generates human-readable and unique booking references based on booking type
 */

export type BookingType = 'rental' | 'driver-hire' | 'agent-assisted' | 'instant' | 'self-drive' | 'with-driver' | 'request-to-book';

interface BookingReferenceConfig {
  prefix: string;
  description: string;
}

// Mapping of booking types to their reference prefixes
const BOOKING_TYPE_PREFIXES: Record<BookingType, BookingReferenceConfig> = {
  'rental': { prefix: 'CR', description: 'Car Rental' },
  'driver-hire': { prefix: 'DH', description: 'Driver Hire' },
  'agent-assisted': { prefix: 'AA', description: 'Agent Assisted' },
  'instant': { prefix: 'IB', description: 'Instant Booking' },
  'self-drive': { prefix: 'SD', description: 'Self Drive' },
  'with-driver': { prefix: 'WD', description: 'With Driver' },
  'request-to-book': { prefix: 'RTB', description: 'Request to Book' }
};

/**
 * Generates a human-readable booking reference from the booking ID and type
 * @param bookingId - The original booking ID from the API
 * @param bookingType - The type of booking
 * @returns A formatted booking reference (e.g., "CR-4521", "DH-8903")
 */
export function generateBookingReference(bookingId: string, bookingType: BookingType): string {
  const config = BOOKING_TYPE_PREFIXES[bookingType];

  // If config is undefined, use a default prefix
  if (!config) {
    const numericPart = extractNumericIdentifier(bookingId);
    return `BK-${numericPart}`;
  }

  // Extract numeric part from the booking ID for better uniqueness
  const numericPart = extractNumericIdentifier(bookingId);

  return `${config.prefix}-${numericPart}`;
}

/**
 * Extracts a meaningful numeric identifier from a booking ID
 * Handles various formats like "TRANSFER1756096341494", "booking-123", "abc123def"
 */
function extractNumericIdentifier(bookingId: string): string {
  // Remove common prefixes and extract numbers
  let cleaned = bookingId.replace(/^(TRANSFER|BOOKING|ORDER|REF)/i, '');
  
  // Extract the last 4-6 digits for uniqueness while keeping it readable
  const numbers = cleaned.match(/\d+/g);
  if (numbers && numbers.length > 0) {
    const allNumbers = numbers.join('');
    // Take last 4 digits, or the whole number if less than 4 digits
    return allNumbers.length > 4 ? allNumbers.slice(-4) : allNumbers;
  }
  
  // Fallback: use the first 4 characters of the original ID (uppercased)
  return bookingId.substring(0, 4).toUpperCase();
}

/**
 * Get the description for a booking type prefix
 */
export function getBookingTypeDescription(bookingType: BookingType): string {
  return BOOKING_TYPE_PREFIXES[bookingType]?.description || 'Unknown';
}

/**
 * Parse a booking reference back to its components
 * @param reference - The booking reference (e.g., "CR-4521")
 * @returns Object with prefix and identifier, or null if invalid format
 */
export function parseBookingReference(reference: string): { prefix: string; identifier: string; type?: BookingType } | null {
  const match = reference.match(/^([A-Z]+)-(.+)$/);
  if (!match) return null;
  
  const [, prefix, identifier] = match;
  
  // Find the booking type that matches this prefix
  const type = Object.entries(BOOKING_TYPE_PREFIXES).find(([, config]) => config.prefix === prefix)?.[0] as BookingType;
  
  return { prefix, identifier, type };
}

/**
 * Validate if a booking reference follows the correct format
 */
export function isValidBookingReference(reference: string): boolean {
  return parseBookingReference(reference) !== null;
}